import type { TableView } from 'owa-mail-list-store';
import { getServerFolderId, getViewFilterForTable, isFilteredTableView } from 'owa-mail-list-store';
import { mutatorAction } from 'satcheljs';
import { lazyFetchSearchFolderIdFromServer } from '../lazyFetchSearchFolderIdFromServer';

export type EnsureSearchFolderIdResult =
    | {
          ok: true;
          searchFolderId: string | undefined;
      }
    | {
          ok: false;
      };

/**
 * Resolves the search-folder id needed to scope a bulk action (Empty,
 * Mark all as read) to the current view. Required to prevent bulk actions
 * from operating on the whole folder instead of a filtered view.
 *
 * - For unfiltered, non-F/O views (and category views), returns ok with
 *   undefined — the bulk action acts on the parent folderId directly.
 * - For filtered or F/O views with an id already cached on the tableView,
 *   returns it.
 * - For filtered or F/O views without a cached id, fetches one from the server
 *   (bypassing IDB) and stashes it on the tableView so downstream code paths see the same
 *   value.
 *
 * Returns `{ ok: false }` only when the on-demand fetch was needed but
 * failed (network error or empty server response). Callers should abort
 * the bulk action and surface an error.
 */
export default async function ensureSearchFolderIdForBulkAction(
    tableView: TableView
): Promise<EnsureSearchFolderIdResult> {
    const cached = getServerFolderId(tableView);
    if (cached) {
        return { ok: true, searchFolderId: cached };
    }

    if (!isFilteredTableView(tableView, getViewFilterForTable(tableView))) {
        return { ok: true, searchFolderId: undefined };
    }

    const fetched = await lazyFetchSearchFolderIdFromServer.importAndExecute(tableView);
    if (!fetched) {
        return { ok: false };
    }

    setServerFolderIdMutator(tableView, fetched);
    return { ok: true, searchFolderId: fetched };
}

const setServerFolderIdMutator = mutatorAction(
    'setServerFolderIdForBulkActionMutator',
    (tableView: TableView, serverFolderId: string) => {
        tableView.serverFolderId = serverFolderId;
    }
);
